import { Link } from "react-router-dom"
import { TableUIRenderCellProps, TextButton, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import SettingsIcon from "@mui/icons-material/Settings"
import dayjs from "dayjs"
import { AssignableCleaning, useAssignPanelContext } from "./AssignPanel"
import { AssignStatus, assignStatusToText } from "@/domain/approvalRequest"
import { CleaningNewStatus } from "@/domain/cleaningNewStatus"
import { CleaningNewStatusChip } from "@/components/CleaningNewStatus"
import { useCompanyAssignment } from "@/hooks/useCompanyAssignment"
import { useCompanyAssignmentStatus } from "@/hooks/useCompanyAssignmentStatus"
import { TableCellProps } from "@/parts/Table/columns/type"
import { TourFlag } from "@/parts/Table/columns/tourFlag"

export const CleaningDateCell = ({
    cell,
    row
}: TableUIRenderCellProps<
    Pick<AssignableCleaning & { tourFlag: TourFlag }, "id" | "tourFlag">,
    dayjs.Dayjs
>) => {
    return (
        <Link
            to={
                row.tourFlag.kind === "photoTour"
                    ? `/operations/${row.id}`
                    : `/cleanings/${row.id}`
            }
        >
            {cell.value.format("YYYY-MM-DD")}
        </Link>
    )
}

export const CleaningStatusCell = ({
    cell: { value: cleaningStatus },
    row
}: TableUIRenderCellProps<
    { cleaningCompanyId?: string },
    CleaningNewStatus | undefined
>) => {
    const { hasAssignedCompany } = useCompanyAssignmentStatus({
        cleaningCompanyId: row.cleaningCompanyId
    })

    return (
        <CleaningNewStatusChip
            status={cleaningStatus}
            hasAssignedCompany={hasAssignedCompany}
        />
    )
}

type CleanerNamesCellRowData = AssignableCleaning & {
    cleaningCompanyId?: string
    cleaningCompanyName?: string
    subStatus: import("@/domain/cleaningSubStatus").SubStatus
}

export const CleanerNamesCell = ({
    cell,
    row
}: TableUIRenderCellProps<
    CleanerNamesCellRowData,
    CleanerNamesCellRowData["cleanerNames"]
>) => {
    const { openPanel } = useAssignPanelContext()
    const { canSelectCompany } = useCompanyAssignment()
    const { isAssignedCompany } = useCompanyAssignmentStatus({
        cleaningCompanyId: row.cleaningCompanyId
    })

    // 業者アサイン機能が有効かつ業者がアサインされている場合は業者名を表示
    const displayNames =
        canSelectCompany && row.cleaningCompanyId
            ? [row.cleaningCompanyName ?? ""]
            : cell.value

    // アサイン対象業者かつ未受託または業者未承認の場合は歯車アイコンを非表示
    const shouldHideGearIcon =
        isAssignedCompany &&
        (row.subStatus === "companyPending" ||
            row.subStatus === "companyUnapproved")

    return (
        <div
            css={css`
                display: grid;
                grid-template-columns: ${
                    shouldHideGearIcon ? "1fr" : "auto 1fr"
                };
                gap: 8px;
                align-items: center;
            `}
        >
            {!shouldHideGearIcon && (
                <TextButton
                    icon={<SettingsIcon />}
                    onClick={() => openPanel(row)}
                />
            )}
            <div
                css={css`
                    display: grid;
                    gap: 8px;
                    justify-content: space-between;
                `}
            >
                {displayNames.map((value, index) => (
                    <span key={index}>{value}</span>
                ))}
            </div>
        </div>
    )
}

export const AddressCell = ({
    cell: { value }
}: TableCellProps<{ address: string }, "address">) => {
    return (
        <Typography
            css={css`
                text-align: left;
            `}
        >
            {value}
        </Typography>
    )
}

export const AssignStatusCell = ({
    cell
}: TableCellProps<{ assignStatus: AssignStatus[] }, "assignStatus">) => {
    return (
        <div
            css={css`
                display: grid;
                gap: 8px;
                justify-content: space-between;
                text-align: right;
            `}
        >
            {cell.value.length > 0
                ? cell.value.map((value, index) => (
                      <span key={index}>{assignStatusToText(value)}</span>
                  ))
                : "未割り当て"}
        </div>
    )
}

export const InspectionCell = ({
    cell
}: TableCellProps<{ hasInspection?: boolean }, "hasInspection">) => {
    return <Typography>{cell.value === true ? "あり" : ""}</Typography>
}
